import React, { forwardRef } from "react";
import DatePicker from "react-datepicker";
import { Controller, useFormContext } from "react-hook-form";
import { InputAdornment } from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import "react-datepicker/dist/react-datepicker.css";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import {
    IExlyCustomDatePickerInput,
    IExlySingleDatePicker,
} from "./ExlySingleDatePicker.interfaces";
import ExlyInput from "../ExlyInput/ExlyInput";

/**
 * ExlyCustomDatePickerInput - This is a custom input for ExlyDatePicker,
 * Reason - As DatePicker does not support, calendar icon on right side,
 * also, the custom input uses ExlyInput, which in turn keeps overall input
 * designs consistant
 */
const ExlyCustomDatePickerInput = forwardRef<
    HTMLInputElement,
    IExlyCustomDatePickerInput
>(({ value, onClick, onBlur, placeholder, classes, showError }, ref) => {
    return (
        <ExlyInput
            onBlur={onBlur}
            classes={{
                root: classes?.inputRoot,
                helperText: classes.helperText,
            }}
            overrideInputProps={{
                ref,
                onClick,
                value: value || "",
                InputProps: {
                    endAdornment: (
                        <InputAdornment position="end">
                            <CalendarMonthRoundedIcon
                                className={classes?.calendarIconStyle}
                            />
                        </InputAdornment>
                    ),
                    placeholder,
                    className: classes?.textStyle,
                    error: showError,
                },
                inputProps: {
                    className: classes?.nativeInputClassName,
                },
                className: classes.inputCustomStyles,
                fullWidth: true,
                autoComplete: "off",
            }}
        />
    );
});

const ExlySingleDatePicker = (props: IExlySingleDatePicker) => {
    const {
        name,
        onChange,
        classes = {},
        CustomErrorMessageComponent,
        registerOptions,
        datePickerProps,
    } = props;

    const {
        control,
        formState: { errors },
    } = useFormContext();
    const error = name ? errors[name] : null;
    const showError = !!error;
    const { message: errorMessage } = error || {};

    return (
        <Controller
            name={name}
            control={control}
            rules={registerOptions}
            render={({
                field: { onChange: onDateChange, onBlur, value, ref },
            }) => (
                <div className={classes.datePickerStyle}>
                    <DatePicker
                        id={name}
                        onChange={date => {
                            onDateChange(date);

                            if (!onChange) return;

                            if (date) {
                                // Only call the parent onChange if the date is not null
                                onChange(date);
                            } else {
                                // Handle the null case (e.g., clear the field, set a default value, etc.)
                                onChange(""); // Or any other appropriate action for your use case
                            }
                        }}
                        onBlur={onBlur}
                        selected={value ? new Date(value) : null}
                        dateFormat="dd/MM/yyyy"
                        customInput={
                            <ExlyCustomDatePickerInput
                                ref={ref}
                                placeholder="dd/mm/yyyy"
                                value={
                                    value
                                        ? new Date(value).toLocaleDateString()
                                        : ""
                                }
                                classes={classes}
                                showError={showError}
                            />
                        }
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        placeholderText="dd/mm/yyyy"
                        {...datePickerProps}
                    />
                    {showError && (
                        <ErrorMessage
                            showError={showError}
                            classes={classes}
                            errorMessage={errorMessage}
                            CustomErrorMessageComponent={
                                CustomErrorMessageComponent
                            }
                        />
                    )}
                </div>
            )}
        />
    );
};

export default ExlySingleDatePicker;
